import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { PACKAGE_OPTIONS, IMAGES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", moving_date: "", delivery_address: "", pickup_address: "", package: PACKAGE_OPTIONS[1], notes: "" };
const PRICES = { "Apartment Package": 89, "Home Package": 149, "Large Home Package": 229 };

const Field = ({ label, name, type = "text", value, onChange, required, placeholder }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">{label}{required && " *"}</label>
    <input
      type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder}
      data-testid={`reserve-${name}`}
      className="w-full bg-transparent border-b border-white/20 rounded-none focus:border-crate-yellow focus:outline-none text-white placeholder-white/30 py-3 transition-colors"
    />
  </div>
);

export default function Reserve() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | pay | done
  const [reservationId, setReservationId] = useState(null);
  const [paypalOn, setPaypalOn] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/config`)
      .then((r) => setPaypalOn(r.data.configured && !!process.env.REACT_APP_PAYPAL_CLIENT_ID))
      .catch(() => setPaypalOn(false));
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const price = PRICES[form.package];
  const payable = price !== undefined;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reservations`, form);
      setReservationId(data.id);
      if (payable && paypalOn) {
        setStep("pay");
      } else {
        setStep("done");
        toast.success("Reservation received! Check your email for confirmation.");
      }
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setForm(EMPTY); setStep("form"); setReservationId(null); };

  return (
    <div data-testid="reserve-page">
      <PageHero overline="Reserve" title="Book your moving totes." subtitle="Fill out the details below and we'll confirm your delivery. It only takes a minute." />

      <section className="mx-auto max-w-7xl px-6 md:px-12 pb-24 md:pb-32 grid lg:grid-cols-[1.4fr_1fr] gap-12">
        <div className="rounded-2xl bg-crate-surface border border-white/10 p-8 md:p-10">
          {step === "done" && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center text-center py-16" data-testid="reserve-success">
              <CheckCircle2 className="h-16 w-16 text-crate-yellow" />
              <h3 className="font-heading font-bold text-2xl text-white mt-6">You're all set, {form.name.split(" ")[0]}!</h3>
              <p className="text-white/60 mt-3 max-w-md">We've received your reservation and sent a confirmation to {form.email}. Our team will reach out shortly to lock in your delivery.</p>
              <button onClick={reset} className="mt-8 rounded-full border border-white/20 text-white font-bold px-6 py-3 hover:border-crate-yellow transition-colors" data-testid="reserve-another-btn">
                Make Another Reservation
              </button>
            </motion.div>
          )}

          {step === "pay" && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="reserve-payment">
              <button onClick={() => setStep("form")} className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-6 transition-colors" data-testid="payment-back-btn">
                <ArrowLeft className="h-4 w-4" /> Back to details
              </button>
              <h3 className="font-heading font-bold text-2xl text-white">Complete your payment</h3>
              <p className="text-white/60 mt-2">Secure checkout with PayPal or card.</p>

              <div className="mt-6 rounded-2xl bg-crate-black border border-white/10 p-6 flex items-center justify-between">
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider">{form.package}</p>
                  <p className="font-heading font-bold text-white text-lg mt-1">Moving {form.moving_date}</p>
                </div>
                <span className="font-heading font-black text-3xl text-crate-yellow">${price}.00</span>
              </div>

              <div className="mt-6">
                <PayPalCheckout pkg={form.package} reservationId={reservationId} onPaid={() => setStep("done")} />
              </div>
              <p className="mt-4 flex items-center gap-2 text-white/40 text-xs"><ShieldCheck className="h-4 w-4 text-crate-yellow" /> Payments are processed securely by PayPal.</p>
            </motion.div>
          )}

          {step === "form" && (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-6" data-testid="reserve-form">
              <Field label="Full Name" name="name" value={form.name} onChange={onChange} required placeholder="Jane Doe" />
              <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} required placeholder="(712) 555-0000" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required placeholder="jane@email.com" />
              <Field label="Moving Date" name="moving_date" type="date" value={form.moving_date} onChange={onChange} required />
              <div className="sm:col-span-2">
                <Field label="Delivery Address" name="delivery_address" value={form.delivery_address} onChange={onChange} required placeholder="Where should we drop off?" />
              </div>
              <div className="sm:col-span-2">
                <Field label="Pickup Address" name="pickup_address" value={form.pickup_address} onChange={onChange} required placeholder="Where should we collect from?" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">Package *</label>
                <select name="package" value={form.package} onChange={onChange} data-testid="reserve-package" className="w-full bg-crate-black border border-white/20 rounded-xl focus:border-crate-yellow focus:outline-none text-white py-3 px-4 transition-colors">
                  {PACKAGE_OPTIONS.map((p) => <option key={p} value={p}>{p}{PRICES[p] ? ` — $${PRICES[p]}` : " — Get a quote"}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">Additional Notes</label>
                <textarea name="notes" value={form.notes} onChange={onChange} rows={3} placeholder="Anything else we should know?" data-testid="reserve-notes" className="w-full bg-transparent border-b border-white/20 focus:border-crate-yellow focus:outline-none text-white placeholder-white/30 py-3 transition-colors resize-none" />
              </div>
              <button type="submit" disabled={loading} data-testid="reserve-submit-btn" className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-crate-yellow text-crate-black font-bold px-8 py-4 hover:bg-white transition-colors disabled:opacity-60">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting...</> : <>{payable && paypalOn ? "Continue to Payment" : "Reserve My Totes"} <ArrowRight className="h-5 w-5" /></>}
              </button>
            </form>
          )}
        </div>

        <div className="rounded-2xl overflow-hidden border border-white/10 relative min-h-[300px]">
          <img src={IMAGES.totesStack} alt="Stacked Color Crate totes" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-crate-black via-crate-black/40 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <p className="font-heading font-bold text-2xl text-white">Free delivery & pickup</p>
            <p className="text-white/70 mt-2">Included with every reservation across our Iowa service area.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
